<template>
  <v-container fluid class="pa-4 bg-light" style="min-height: 90vh;">
    <!-- Encabezado de la página -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <ModuleHeader>Registro de Actividad</ModuleHeader>
        <div class="text-caption grey--text mt-n2">Monitoreo de acciones y cambios realizados por los usuarios en el
          sistema</div>
      </div>
    </div>

    <v-card class="pgs-modern-card mb-6" elevation="0">

      <!-- Búsqueda en Servidor -->
      <v-card-text class="border-bottom pb-4 pt-5">
        <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-3 text-uppercase">Consulta de Rango de
          Fechas </div>
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-menu v-model="menuStart" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="serverQuery.startDate" placeholder="Fecha Inicial"
                  prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" outlined dense hide-details
                  class="modern-input" color="primary"></v-text-field>
              </template>
              <v-date-picker v-model="serverQuery.startDate" @input="menuStart = false" color="primary"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <v-menu v-model="menuEnd" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="serverQuery.endDate" placeholder="Fecha Final" prepend-inner-icon="mdi-calendar"
                  readonly v-bind="attrs" v-on="on" outlined dense hide-details class="modern-input"
                  color="primary"></v-text-field>
              </template>
              <v-date-picker v-model="serverQuery.endDate" @input="menuEnd = false" color="primary"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <v-btn color="primary" elevation="0" large class="rounded-lg text-none font-weight-bold"
              @click="fetchLogsFromServer" :loading="loading" block>
              <v-icon left>mdi-cloud-search</v-icon> Buscar Logs
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Sección de Filtros Locales -->
      <v-card-text class="border-bottom bg-grey-lighten-4 pt-4 pb-4">
        <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-3 text-uppercase">Filtros
        </div>
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-text-field v-model="filters.search" placeholder="Buscar texto, detalle o ID..."
              prepend-inner-icon="mdi-magnify" dense outlined hide-details class="modern-input" color="primary"
              clearable></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select v-model="filters.actionType" :items="actionTypes" placeholder="Tipo de Acción" dense outlined
              hide-details class="modern-input" color="primary" clearable>
              <template v-slot:selection="{ item }">
                <v-chip :color="getActionColor(item)" text-color="white" x-small class="font-weight-bold">
                  {{ item }}
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <v-chip :color="getActionColor(item)" text-color="white" x-small class="font-weight-bold mr-2">
                  {{ item }}
                </v-chip>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select v-model="filters.user" :items="usersList" placeholder="Usuario" dense outlined hide-details
              class="modern-input" color="primary" clearable></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select v-model="filters.module" :items="modules" placeholder="Módulo" dense outlined hide-details
              class="modern-input" color="primary" clearable></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="2" class="text-right">
            <v-btn color="grey darken-2" text class="text-none font-weight-bold" @click="resetFilters">
              <v-icon left small>mdi-filter-remove-outline</v-icon> Limpiar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Tabla de Logs -->
      <v-data-table :headers="headers" :items="filteredLogs" :loading="loading" :items-per-page.sync="itemsPerPage"
        :page.sync="page" class="logs-table" disable-sort>
        <template v-slot:item.ev="{ item }">
          <v-chip :color="getActionColor(item.ev)" text-color="white" small class="font-weight-bold">
            {{ item.ev }}
          </v-chip>
        </template>

        <template v-slot:item.tc="{ item }">
          <div class="text-caption font-weight-medium grey--text text--darken-3">
            {{ formatTime(item.tc) }}
          </div>
        </template>

        <template v-slot:item.ac="{ item }">
          <div class="d-flex align-center">
            <v-avatar color="primary lighten-4" size="24" class="mr-2">
              <span class="primary--text text-caption font-weight-bold">{{ item.ac && item.ac.nm ? item.ac.nm.charAt(0)
                : 'D' }}</span>
            </v-avatar>
            <div class="text-body-2 font-weight-bold grey--text text--darken-3">
              {{ item.ac && item.ac.nm ? item.ac.nm : "Desconocido" }}
            </div>
          </div>
        </template>

        <template v-slot:item.cx="{ item }">
          <div class="text-caption font-weight-bold grey--text">
            {{ item.cx && item.cx.dp ? item.cx.dp.nm : "General" }}
          </div>
        </template>

        <template v-slot:item.rf="{ item }">
          <div v-if="getRf(item)" class="py-1">
            <div v-if="typeof getRf(item) === 'object'">
              <div class="text-body-2 font-weight-bold grey--text text--darken-3">
                {{ getRf(item).al || getRf(item).nm || getRf(item).nombre || getRf(item).id || '—' }}
              </div>
              <div class="d-flex align-center flex-wrap mt-1" style="gap: 4px;">
                <v-chip v-if="getRf(item).tp" x-small label color="blue-grey lighten-5" text-color="blue-grey darken-3"
                  class="font-weight-medium">
                  {{ getRf(item).tp }}
                </v-chip>
                <span v-if="getRf(item).id && (getRf(item).al || getRf(item).nm || getRf(item).nombre)"
                  class="text-caption grey--text text--darken-1" :title="getRf(item).id">
                  ID: {{ getRf(item).id }}
                </span>
              </div>
            </div>
            <div v-else class="text-body-2 font-weight-medium">
              {{ getRf(item) }}
            </div>
          </div>
          <span v-else class="grey--text text-caption">—</span>
        </template>

        <template v-slot:item.ms="{ item }">
          <div class="text-body-2 font-weight-medium">
            {{ item.ms }}
          </div>
          <div v-if="getDataName(item)" class="text-caption mt-1 d-flex align-center">
            <span class="font-weight-bold primary--text mr-1">Nombre:</span>
            <span class="grey--text text--darken-3 font-weight-medium">{{ getDataName(item) }}</span>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon color="primary" small @click="viewLogDetails(item)">
            <v-icon>mdi-eye</v-icon>
          </v-btn>
        </template>

        <!-- Empty state -->
        <template v-slot:no-data>
          <div class="pa-10 text-center">
            <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-text-box-search-outline</v-icon>
            <h3 class="grey--text text--darken-1 font-weight-medium">No se encontraron registros</h3>
            <p class="text-caption grey--text">Intenta ajustar tus filtros de búsqueda.</p>
          </div>
        </template>
      </v-data-table>


    </v-card>

    <!-- Modal de Detalles -->
    <v-dialog v-model="dialogVisible" max-width="700">
      <v-card class="pgs-modern-card" elevation="0">
        <v-card-title class="d-flex align-center justify-space-between border-bottom pb-3">
          <div class="d-flex align-center">
            <v-chip :color="getActionColor(selectedLog?.ev)" text-color="white" small class="font-weight-bold mr-3">
              {{ selectedLog?.ev }}
            </v-chip>
            <span class="text-h6 font-weight-bold">Detalle de Actividad</span>
          </div>
          <v-btn icon @click="dialogVisible = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="pt-5 pb-5">
          <v-row class="mb-4">
            <v-col cols="12" sm="3">
              <div class="text-caption font-weight-bold grey--text text-uppercase mb-1">Fecha y Hora</div>
              <div class="text-body-2 font-weight-medium">{{ formatTime(selectedLog?.tc) }}</div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="text-caption font-weight-bold grey--text text-uppercase mb-1">Usuario</div>
              <div class="text-body-2 font-weight-medium">{{ selectedLog?.ac?.nm || 'Desconocido' }}</div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="text-caption font-weight-bold grey--text text-uppercase mb-1">Módulo</div>
              <div class="text-body-2 font-weight-medium">{{ selectedLog?.cx?.dp?.nm || 'General' }}</div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="text-caption font-weight-bold grey--text text-uppercase mb-1">Referencia</div>
              <div v-if="getRf(selectedLog)" class="text-body-2 font-weight-medium">
                {{ typeof getRf(selectedLog) === 'object' ? (getRf(selectedLog).al || getRf(selectedLog).nm ||
                  getRf(selectedLog).id || getRf(selectedLog).tp) : getRf(selectedLog) }}
              </div>
              <div v-else class="text-body-2 grey--text">—</div>
            </v-col>
          </v-row>

          <div class="mb-4">
            <div class="text-caption font-weight-bold grey--text text-uppercase mb-1">Descripción de la Acción</div>
            <div class="text-body-1 font-weight-medium primary--text">{{ selectedLog?.ms }}</div>
            <div v-if="getDataName(selectedLog)" class="mt-2 text-body-2">
              <span class="font-weight-bold grey--text text--darken-2">Nombre / Elemento: </span>
              <span class="font-weight-bold primary--text">{{ getDataName(selectedLog) }}</span>
            </div>
          </div>

          <div>
            <div class="text-caption font-weight-bold grey--text text-uppercase mb-1">Detalles Técnicos / Cambios (JSON)
            </div>
            <v-data-table v-if="parsedDetails.length > 0"
              :headers="[{ text: 'Propiedad', value: 'key', width: '30%' }, { text: 'Valor', value: 'value' }]"
              :items="parsedDetails" hide-default-footer disable-sort dense class="elevation-1">
              <template v-slot:item.value="{ item }">
                <pre class="text-caption pa-2"
                  style="white-space: pre-wrap; font-family: monospace; color: #333; max-height: 200px; overflow-y: auto;">{{ item.value }}</pre>
              </template>
            </v-data-table>
            <div v-else class="text-caption grey--text">Sin detalles adicionales</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: "LoggerView",
  data() {
    return {
      loading: false,
      dialogVisible: false,
      selectedLog: null,
      menuStart: false,
      menuEnd: false,
      serverQuery: {
        startDate: "",
        endDate: "",
      },
      filters: {
        search: "",
        actionType: null,
        user: null,
        module: null,
      },
      headers: [
        { text: "Acción", value: "ev", width: "110px" },
        { text: "Fecha y Hora", value: "tc", width: "150px" },
        { text: "Usuario", value: "ac", width: "150px" },
        { text: "Módulo", value: "cx", width: "140px" },
        { text: "Referencia", value: "rf", width: "190px" },
        { text: "Descripción", value: "ms" },
        { text: "Ver", value: "actions", width: "60px", align: "center" },
      ],
      // Datos de prueba orientados a auditoría
      logs: [],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  computed: {
    actionTypes() {
      const set = new Set();
      this.logs.forEach(log => {
        if (log && log.ev) set.add(log.ev);
      });
      return Array.from(set).sort();
    },
    modules() {
      const set = new Set();
      this.logs.forEach(log => {
        const mod = log && log.cx && log.cx.dp && log.cx.dp.nm ? log.cx.dp.nm : null;
        if (mod) set.add(mod);
      });
      return Array.from(set).sort();
    },
    usersList() {
      const set = new Set();
      this.logs.forEach(log => {
        const user = log && log.ac && log.ac.nm ? log.ac.nm : null;
        if (user) set.add(user);
      });
      return Array.from(set).sort();
    },
    filteredLogs() {
      return this.logs.filter(log => {
        const desc = log.ms || "";
        const user = log.ac && log.ac.nm ? log.ac.nm : "";
        const rfObj = this.getRf(log);
        const rfStr = rfObj && typeof rfObj === 'object'
          ? `${rfObj.al || ''} ${rfObj.tp || ''} ${rfObj.id || ''} ${rfObj.nm || ''}`
          : String(rfObj || '');
        const dtName = this.getDataName(log);

        const searchLower = this.filters.search ? this.filters.search.toLowerCase() : "";
        const matchSearch = !searchLower ||
          desc.toLowerCase().includes(searchLower) ||
          user.toLowerCase().includes(searchLower) ||
          rfStr.toLowerCase().includes(searchLower) ||
          dtName.toLowerCase().includes(searchLower);

        const matchAction = !this.filters.actionType || log.ev === this.filters.actionType;

        const matchUser = !this.filters.user || user === this.filters.user;

        const moduleVal = log.cx && log.cx.dp && log.cx.dp.nm ? log.cx.dp.nm : "";
        const matchModule = !this.filters.module || moduleVal === this.filters.module;

        return matchSearch && matchAction && matchUser && matchModule;
      });
    },
    parsedDetails() {
      if (!this.selectedLog || !this.selectedLog.dt) return [];
      try {
        let parsed = this.selectedLog.dt;
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }

        return Object.entries(parsed).map(([key, value]) => ({
          key,
          value: typeof value === 'object' ? JSON.stringify(value, null, 2) : value
        }));
      } catch (e) {
        return [{ key: 'Raw', value: this.selectedLog.dt }];
      }
    }
  },
  mounted() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const dateStr = `${yyyy}-${mm}-${dd}`;

    this.serverQuery.startDate = dateStr;
    this.serverQuery.endDate = dateStr;
  },
  methods: {
    async getLogs() {
      const tenant = this.$store.getters.tenant;
      const fechaInicial = this.serverQuery.startDate;
      const fechaFinal = this.serverQuery.endDate;
      const queryParams = `?tenant=${tenant}&fechaInicial=${fechaInicial}&fechaFinal=${fechaFinal}`;

      await this.$store.getters.getFirebaseApp.fba
        .call(`GBL-Registros-leerRegistros${queryParams}`, {})
        .then((result) => {
          if (result && result.data && result.data.ok) {
            this.logs = result.data.data;
          }
        })
        .catch((error) => {
          console.log(error)
        });
    },
    async fetchLogsFromServer() {
      this.loading = true;
      await this.getLogs()
      this.loading = false;

    },
    resetFilters() {
      this.filters = {
        search: "",
        actionType: null,
        user: null,
        module: null
      };
    },
    viewLogDetails(log) {
      this.selectedLog = log;
      this.dialogVisible = true;
    },
    getActionColor(actionType) {
      if (!actionType) return 'grey';
      const action = String(actionType).toUpperCase();
      if (action.includes('CREATE') || action.includes('CREAR')) {
        return 'green darken-1';
      }
      if (action.includes('EDIT') || action.includes('UPDATE') || action.includes('ACTUALIZAR')) {
        return 'blue darken-1';
      }
      if (action.includes('DELETE') || action.includes('ELIMINAR') || action.includes('REMOVE')) {
        return 'red darken-1';
      }
      if (action.includes('LOGIN')) {
        return 'purple lighten-1';
      }
      if (action.includes('EXPORT')) {
        return 'orange darken-2';
      }
      return 'grey darken-1';
    },
    formatTime(isoString) {
      if (!isoString) return "";
      const date = new Date(isoString);
      return date.toLocaleString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },
    getRf(item) {
      if (!item) return null;
      if (item._rf !== undefined) return item._rf;

      let rf = null;
      if (item.rf) {
        if (typeof item.rf === 'string') {
          try { rf = JSON.parse(item.rf); } catch (e) { rf = item.rf; }
        } else {
          rf = item.rf;
        }
      } else if (item.op && item.op.rf) {
        rf = item.op.rf;
      } else if (item.dt) {
        try {
          const parsed = typeof item.dt === 'string' ? JSON.parse(item.dt) : item.dt;
          if (parsed && parsed.op && parsed.op.rf) {
            rf = parsed.op.rf;
          } else if (parsed && parsed.rf) {
            rf = parsed.rf;
          }
        } catch (e) {
          // ignore error
        }
      }
      this.$set(item, '_rf', rf);
      return rf;
    },
    getDataName(item) {
      if (!item) return "";
      if (item._dtName !== undefined) return item._dtName;

      let name = "";
      try {
        let dtObj = item.dt;
        if (typeof dtObj === "string") {
          dtObj = JSON.parse(dtObj);
        }
        if (dtObj) {
          const target = dtObj.data || dtObj;
          if (Array.isArray(target)) {
            const names = target
              .map(t => t && (t.nombre || t.Nombre || t.nm || t.Nm || t.NOMBRE || t.NM))
              .filter(Boolean);
            if (names.length) name = names.join(", ");
          } else if (typeof target === "object" && target !== null) {
            name = target.nombre || target.Nombre || target.nm || target.Nm || target.NOMBRE || target.NM || "";
          }
        }
      } catch (e) {
        // ignore error
      }

      if (!name && item.data && typeof item.data === "object") {
        name = item.data.nombre || item.data.Nombre || item.data.nm || item.data.Nm || item.data.NOMBRE || item.data.NM || "";
      }

      this.$set(item, "_dtName", name);
      return name;
    }
  },
};
</script>

<style scoped>
.bg-light {
  background-color: #f1f5f9;
}

.bg-grey-lighten-4 {
  background-color: #f8fafc !important;
}

.pgs-modern-card {
  background-color: white;
  border-radius: 16px !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0;
}

.border-top {
  border-top: 1px solid #e2e8f0;
}

/* Custom table styling to make it look clean */
.logs-table>>>.v-data-table__wrapper {
  border-radius: 0;
}

.logs-table>>>th {
  background-color: white !important;
  color: #64748b !important;
  text-transform: uppercase;
  font-size: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e2e8f0 !important;
}

.logs-table>>>td {
  border-bottom: 1px solid #f1f5f9 !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.logs-table>>>tbody tr:hover {
  background-color: #f8fafc !important;
}
</style>
